import { getCurrencySymbol } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AccountGroup } from '@galaxy/account-group';
import {
  ItemPricingTableConfig,
  ItemPricingTableSelectorConfig,
  ItemRowChangeEvent,
  ItemsChangedEvent,
  ItemSelectorConfig,
  ItemSelectorFilterType,
  UpdateLineItemFromItemRow,
} from '@galaxy/inventory-ui';
import { App } from '@galaxy/marketplace-apps';
import { FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Package } from '@vendasta/marketplace-packages';
import { LineItem, Revenue, RevenueComponent, RevenuePeriod, SalesOrdersService } from '@vendasta/sales-orders';
import { SalesOrderLineItemSelectorDialogComponent } from '@vendasta/sales-ui';
import { OrderFormOptionsInterface } from '@vendasta/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { UILineItemWithEditionPricing } from '../../shared/interface';
import { PeriodOptions } from './line-items.interface';
import { LineItemsService } from './line-items.service';
import { inventoryItemsToLineItems } from './utils';

const ITEM_PRICING_TABLE_ORDERS = 'item_pricing_table_orders';

@Component({
  selector: 'orders-line-items',
  templateUrl: './line-items.component.html',
  styleUrls: ['./line-items.component.scss'],
})
export class LineItemsComponent implements OnInit, OnDestroy {
  @Input() business: Pick<AccountGroup, 'externalIdentifiers' | 'napData' | 'accountGroupId'>;

  // we specify line items to populate the component when an opportunity is
  // selected, or when we are editing an existing order
  @Input() specifiedLineItems: LineItem[];

  // configurations
  hideSellingStandaloneProducts = false;
  // allows multiple quantities to be set for products that are not multi-activatable
  @Input() bypassQuantityRestrictions = false;
  @Input() allowNegativeValues = false;
  @Input() editable = false;
  @Input() startEditing = false;
  @Input() currencyOverride: string = null;
  @Input() allowRemovingLastLineItem: boolean;

  @Output() lineItemsUpdated: EventEmitter<UILineItemWithEditionPricing[]> = new EventEmitter<
    UILineItemWithEditionPricing[]
  >();
  @Output() lineItemsInitialized: EventEmitter<UILineItemWithEditionPricing[]> = new EventEmitter<
    UILineItemWithEditionPricing[]
  >();

  editing = false;
  loading$: Observable<boolean>;
  formArrayLineItems: UntypedFormArray;
  currentLineItems$$: BehaviorSubject<UILineItemWithEditionPricing[]> = new BehaviorSubject<
    UILineItemWithEditionPricing[]
  >([]);
  currentLineItems: UILineItemWithEditionPricing[] = [];
  uiLineItems$: Observable<UILineItemWithEditionPricing[]>;
  orderFormOptions$: Observable<OrderFormOptionsInterface>;
  featureFlags$: Observable<FeatureFlagStatusInterface>;

  useInventoryPricingTableSelector = signal(false);
  useInventoryItemPricingTableSelector$: Observable<boolean>;
  itemPricingTableSelectorConfig$: Observable<ItemPricingTableSelectorConfig>;

  // pricing related
  periodOptions: PeriodOptions[] = [
    { label: 'FRONTEND.STORE.PERIODS.ONE_TIME', value: RevenuePeriod.ONETIME },
    { label: 'FRONTEND.STORE.PERIODS.MONTHLY', value: RevenuePeriod.MONTHLY },
    { label: 'FRONTEND.STORE.PERIODS.YEARLY', value: RevenuePeriod.YEARLY },
  ];

  oneTimeOnlyOptions: PeriodOptions[] = [{ label: 'FRONTEND.STORE.PERIODS.ONE_TIME', value: RevenuePeriod.ONETIME }];

  private subscriptions: Subscription[] = [];

  constructor(
    private formbuilder: UntypedFormBuilder,
    private cdk: ChangeDetectorRef,
    private dialog: MatDialog,
    private alertService: SnackbarService,
    private readonly salesOrdersService: SalesOrdersService,
    private readonly lineItemsService: LineItemsService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.editing = !!this.editable && !!this.startEditing;
    this.lineItemsService.initialize(this.business, this.specifiedLineItems);
    this.uiLineItems$ = this.lineItemsService.uiLineItems$.pipe(shareReplay(1));
    this.loading$ = this.lineItemsService.loading$;
    const orderConfig$ = this.salesOrdersService
      .getConfig(this.business.externalIdentifiers.partnerId, this.business.externalIdentifiers.marketId)
      .pipe(shareReplay(1));
    const hideSellingStandaloneProducts$ = orderConfig$.pipe(
      map((config) => config.salespersonOptions?.disableSellingStandaloneProducts || false),
    );
    this.subscriptions.push(
      hideSellingStandaloneProducts$.subscribe((hideSellingStandaloneProducts) => {
        this.hideSellingStandaloneProducts = hideSellingStandaloneProducts;
      }),
    );
    this.formArrayLineItems = this.formbuilder.array([]);
    this.subscriptions.push(
      this.uiLineItems$
        .pipe(
          map((uiLineItems) => {
            this.setCurrentLineItems(uiLineItems);
            this.lineItemsInitialized.emit(uiLineItems);
            this.formArrayLineItems = this.buildFormArrayFromLineItems(uiLineItems);
            this.cdk.detectChanges();
          }),
          tap(() => this.lineItemsService.loading$$.next(false)),
        )
        .subscribe(),
    );
    this.orderFormOptions$ = orderConfig$.pipe(
      map((config): OrderFormOptionsInterface => {
        return {
          readOnly: false,
          showOfficeUseQuestions: true,
          bypassRequiredQuestions: !config.salespersonOptions.validateRequiredFields,
        };
      }),
    );

    this.featureFlags$ = this.featureFlagService
      .batchGetStatus(this.business.externalIdentifiers.partnerId, this.business.externalIdentifiers.marketId, [
        ITEM_PRICING_TABLE_ORDERS,
      ])
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.useInventoryItemPricingTableSelector$ = this.featureFlags$.pipe(
      map((flags) => flags[ITEM_PRICING_TABLE_ORDERS]),
      tap((useInventoryPricingTable) => this.useInventoryPricingTableSelector.set(useInventoryPricingTable)),
    );

    this.itemPricingTableSelectorConfig$ = hideSellingStandaloneProducts$.pipe(
      map((hideSellingStandaloneProducts) => {
        return {
          itemPricingTableConfig: this.itemPricingTableConfig(),
          itemSelectorConfig: this.itemSelectorConfig(hideSellingStandaloneProducts),
        };
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  deleteLineItem(index: number): void {
    if (!this.allowRemovingLastLineItem && this.currentLineItems.length === 1) {
      this.alertService.openErrorSnack('COMMON.ORDERS.ERROR_AT_LEAST_ONE_LINE_ITEM');
      return;
    }
    this.currentLineItems.splice(index, 1);
    this.setCurrentLineItems(this.currentLineItems);
    this.formArrayLineItems.removeAt(index);
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  duplicateLineItem(index: number): void {
    const duplicateLineItem = structuredClone(this.currentLineItems[index]);
    this.currentLineItems.splice(index + 1, 0, duplicateLineItem);
    this.setCurrentLineItems(this.currentLineItems);
    // update line items in the UI and form
    this.formArrayLineItems = this.buildFormArrayFromLineItems(this.currentLineItems);
    this.cdk.detectChanges();
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  addLineItems(): void {
    const preselectedPackages = this.currentLineItems
      .filter((item) => !!item.lineItem?.packageId)
      .map((item) => new Package({ packageId: item.lineItem.packageId }));
    const preselectedApps = this.currentLineItems
      .filter((item) => !!item.lineItem?.appKey?.appId)
      .map(
        (item) =>
          new App({
            key: {
              appId: item.lineItem.appKey.appId,
              editionId: item.lineItem.appKey.editionId,
            },
            parentRequirements: {
              parentDetails: {
                key: {
                  appId: item.parentAppId,
                },
                name: item.parentName,
              },
            },
            appType: item.appType,
          }),
      );

    const dialogRef = this.dialog.open(SalesOrderLineItemSelectorDialogComponent, {
      data: {
        marketId: this.business.externalIdentifiers.marketId,
        preselectedApps: preselectedApps,
        preselectedPackages: preselectedPackages,
        hideSellingStandaloneProducts: this.hideSellingStandaloneProducts,
        hideOwnerName: true,
        itemSelectorIncludesPackages: true, // TODO - remove this when the item selector defaults to showing packages
      },
      width: '1000px',
      maxWidth: '95vw',
      height: '900px',
      minHeight: '420px',
      maxHeight: '85vh',
    });
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((newlySelectedLineItems) => !!newlySelectedLineItems),
        tap(() => this.updateFormValuesWithEditingItems()),
        switchMap((newlySelectedLineItems) => {
          return this.lineItemsService.resolveLineItemSelectorInteractions(
            preselectedPackages,
            preselectedApps,
            newlySelectedLineItems,
            this.currentLineItems,
          );
        }),
        switchMap((newlySelectedLineItems) => {
          this.setCurrentLineItems(newlySelectedLineItems);
          // update line items in the UI and form
          this.formArrayLineItems = this.buildFormArrayFromLineItems(newlySelectedLineItems);
          this.subscriptions.push(
            this.formArrayLineItems.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
              this.updateFormValuesWithEditingItems();
            }),
          );
          return newlySelectedLineItems;
        }),
      )
      .subscribe(() => {
        // scroll view to the top of the line item editor as updated list of items increases the vertical height of the view
        document.getElementById('top').scrollIntoView({ behavior: 'smooth' });
        this.cdk.detectChanges();
        this.lineItemsUpdated.emit(this.currentLineItems);
      });
  }

  updateFormValuesWithEditingItems(): void {
    // bypass forms when using inventory pricing table
    if (this.useInventoryPricingTableSelector()) return;

    this.currentLineItems.map((uiLineItem, index) => {
      const lineItemFormGroup = this.formArrayLineItems.controls[index] as UntypedFormGroup;
      uiLineItem.lineItem.quantity = lineItemFormGroup.controls.quantity.value;
      const revenueComponentsFormArray = lineItemFormGroup.controls?.revenueComponents as UntypedFormArray;
      const revenueComponents = revenueComponentsFormArray.controls.map((fa) => {
        const fg = fa as UntypedFormGroup;
        return new RevenueComponent({
          value: Math.round(fg.controls.revenue.value * 100),
          period: fg.controls.period.value,
          isStartingRevenue: fg.controls.startingAt.value,
        });
      });
      uiLineItem.lineItem.currentRevenue = new Revenue({ revenueComponents: revenueComponents });
    });
  }

  // builds the form group out of the selected items
  buildFormArrayFromLineItems(lineItems: UILineItemWithEditionPricing[]): UntypedFormArray {
    const newForm = this.formbuilder.array(
      lineItems.map((li) => {
        const revenueControls = this.revenueControlFromLineItemRevenue(li.lineItem?.currentRevenue);
        const quantityEnabled = this.bypassQuantityRestrictions || (li.isMultiActivatable && !li.usesCustomPricing);
        return this.formbuilder.group({
          quantity: [
            {
              value: quantityEnabled ? li.lineItem.quantity : 1,
              disabled: !quantityEnabled,
            },
            [Validators.min(1)],
          ],
          revenueComponents: this.formbuilder.array(revenueControls),
        });
      }),
    );

    // TODO: This is a workaround for the form array being rebuilt when adding and removing items. We will probably want
    //  to take the time in the future refactoring this component so it doesn't leak subscriptions
    this.subscriptions.push(
      newForm.valueChanges.pipe(debounceTime(500)).subscribe(() => this.lineItemsUpdated.emit(this.currentLineItems)),
    );

    return newForm;
  }

  revenueControlFromLineItemRevenue(revenue: Revenue): UntypedFormGroup[] {
    return (revenue?.revenueComponents || []).map((rc) =>
      this.formbuilder.group({
        revenue: [(rc.value || 0) / 100, [Validators.required, this.validateRevenue.bind(this)]],
        period: [rc.period === undefined ? RevenuePeriod.ONETIME : rc.period, [Validators.required]],
        startingAt: [rc.isStartingRevenue],
      }),
    );
  }

  validateRevenue(c: UntypedFormControl): ValidationErrors | null {
    const revenueValue = c.value;
    if (isNaN(revenueValue)) {
      return { pattern: true };
    }
    if (Number(revenueValue) < 0 && this.allowNegativeValues === false) {
      return { min: true };
    }
    return null;
  }

  //
  // NOTE:
  // The following methods are used from page components with a ViewChild reference to this component
  //

  // check that the form of the component is in a valid state
  public isFormValid(): boolean {
    const valid = this.formArrayLineItems.valid;
    if (!valid) {
      this.formArrayLineItems.markAsTouched();
      this.formArrayLineItems.updateValueAndValidity();
    }
    if (this.currentLineItems.length <= 0) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_MUST_HAVE_ITEMS');
      return false;
    }
    if (this.currentLineItems.find((item) => item.lineItem?.currentRevenue?.revenueComponents.length === 0)) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_MUST_HAVE_PRICING');
      return false;
    }

    return valid;
  }

  // retrieve the line items currently stored in the component
  public getCurrentLineItems(): LineItem[] {
    this.updateFormValuesWithEditingItems();
    return this.currentLineItems.map((li) => {
      const item = new LineItem(li.lineItem);
      if (this.currencyOverride) {
        item.currencyCode = this.currencyOverride;
      }
      return item;
    });
  }

  public currencySymbol(currencyCode: string): string {
    return getCurrencySymbol(currencyCode, 'wide');
  }

  addRevenueRow(liIndex: number): void {
    const components = this.currentLineItems[liIndex].lineItem.currentRevenue.revenueComponents;
    components.push(new RevenueComponent());
    this.formArrayLineItems = this.buildFormArrayFromLineItems(this.currentLineItems);
    this.subscriptions.push(
      this.formArrayLineItems.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
        this.updateFormValuesWithEditingItems();
      }),
    );
    this.cdk.detectChanges();
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  deleteRevenueRow(liIndex: number, rcIndex: number): void {
    if (
      !this.allowRemovingLastLineItem &&
      this.currentLineItems[liIndex].lineItem.currentRevenue.revenueComponents.length === 1 &&
      this.currentLineItems.length === 1
    ) {
      this.alertService.openErrorSnack('COMMON.ORDERS.ERROR_AT_LEAST_ONE_LINE_ITEM');
      return;
    }

    this.currentLineItems[liIndex].lineItem.currentRevenue.revenueComponents.splice(rcIndex, 1);
    if (this.currentLineItems[liIndex].lineItem.currentRevenue.revenueComponents.length === 0) {
      this.currentLineItems.splice(liIndex, 1);
    }
    this.formArrayLineItems = this.buildFormArrayFromLineItems(this.currentLineItems);
    this.updateFormValuesWithEditingItems();
    this.subscriptions.push(
      this.formArrayLineItems.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
        this.updateFormValuesWithEditingItems();
      }),
    );
    this.cdk.detectChanges();
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  private setCurrentLineItems(uiLineItems: UILineItemWithEditionPricing[]): void {
    // Sort the revenue components to ensure one time always appears last, initially
    uiLineItems?.forEach?.((uilineItem) => {
      uilineItem.lineItem?.currentRevenue?.revenueComponents?.sort((a: RevenueComponent, b: RevenueComponent) => {
        if (a.period === RevenuePeriod.ONETIME || !a.period) {
          return 1;
        }
        if (b.period === RevenuePeriod.ONETIME || !b.period) {
          return -1;
        }
        return 0;
      });
    });
    this.currentLineItems = uiLineItems;
    this.currentLineItems$$.next(uiLineItems);
  }

  updateLineItemFromInventoryItem(event: ItemRowChangeEvent): void {
    if (event.action === 'add') {
      this.duplicateLineItem(event.item.index);
    } else if (event.action === 'update') {
      this.currentLineItems[event.item.index].lineItem = UpdateLineItemFromItemRow(
        this.currentLineItems[event.item.index].lineItem,
        event.item,
      );
    } else if (event.action === 'delete') {
      this.deleteLineItem(event.item.index);
      return;
    }
    this.setCurrentLineItems(this.currentLineItems);
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  updateLineItemsFromInventoryLineItems(changedEvent: ItemsChangedEvent): void {
    let updateFn: (currentLineItems: UILineItemWithEditionPricing[]) => UILineItemWithEditionPricing[];
    switch (changedEvent.type) {
      case 'add':
        updateFn = (currentLineItems) => {
          const newItems = changedEvent.items;
          const newLineItems = inventoryItemsToLineItems(newItems);
          newItems.forEach((newItem, i) => {
            currentLineItems.splice(newItem.index, 0, newLineItems[i]);
          });
          return currentLineItems;
        };
        break;
      case 'update':
        updateFn = (currentLineItems) => {
          const changedItems = changedEvent.items;
          const changedLineItems = inventoryItemsToLineItems(changedItems);
          changedItems.forEach((changedItem, i) => {
            const lineItem = changedLineItems[i];
            currentLineItems[changedItem.index] = lineItem;
          });
          return currentLineItems;
        };
        break;
      case 'delete':
        changedEvent.items.forEach((item) => this.deleteLineItem(item.index));
        break;
    }
    if (updateFn) {
      this.setCurrentLineItems(updateFn(this.currentLineItems));
    }
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  private itemPricingTableConfig(): ItemPricingTableConfig {
    return {
      partnerId: this.business.externalIdentifiers.partnerId,
      marketId: this.business.externalIdentifiers.marketId,
      businessId: this.business.accountGroupId,
      selectionEditable: true,
      quantityEditable: true,
      billingTermsEnabled: true,
      hideColumns: ['wholesale_price', 'retail_price_total'],
      hidePriceSummary: true,
      hideBorder: true,
    };
  }

  private itemSelectorConfig(hideSellingStandaloneProducts: boolean): ItemSelectorConfig {
    let itemTypesAvailable: ItemSelectorFilterType[] = ['products', 'packages'];
    if (hideSellingStandaloneProducts) {
      itemTypesAvailable = itemTypesAvailable.filter((itemType) => itemType !== 'products');
    }
    return {
      partnerId: this.business.externalIdentifiers.partnerId,
      marketId: this.business.externalIdentifiers.marketId,
      businessId: this.business.accountGroupId,
      pageSize: 15,
      hideOwnerName: true,
      hideWholesale: true,
      hideRetail: false,
      hideQuantity: true,
      hideItemsWithOrderForms: false,
      itemTypesAvailable: itemTypesAvailable,
      enforceDependencies: true,
    };
  }
}
